/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect, useState } from 'react';
import { Button, Icon, FileDrop } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { useFileUploadContext } from '../../../../context/FileUploadContext/FileUploadContext';
import FileUploadAction from '../../../../context/FileUploadContext/FileUploadActions';
import {
  fileUploadSizeDecimal,
  MAGIC_VALUE,
  ManualFileUploadTypeList
} from '../../../shared/Utils/Constants';
import { formatBytes, isCsvInvalid } from '../../../shared/Utils/Utils';
interface IFile extends File {
  preview: string;
}

export const uploadTypeCheck = (SelectedDataTye: string) =>
  ManualFileUploadTypeList.find((r) => r.type === SelectedDataTye)
    ?.allowedFiles[0]?.uploadType || '';

export const minSizeCheck = (SelectedDataTye: string) =>
  ManualFileUploadTypeList.find((r) => r.type === SelectedDataTye)
    ?.allowedFiles[0]?.minSize || 0;

export const maxSizeCheck = (SelectedDataTye: string) =>
  ManualFileUploadTypeList.find((r) => r.type === SelectedDataTye)
    ?.allowedFiles[0]?.maxSize || 0;

const parseFileSize = (size: number | undefined) => {
  if (!size) return 0;
  return size;
};

const getFileAcceptedTypes = (selectedDataType: string) =>
  selectedDataType === 'utilityBill' ? '.csv,.urj' : '.csv';

function UploadTab(props: { selectedDataType: string }) {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const [state, dispatch] = useFileUploadContext();
  let acceptedFiles: IFile[] = [],
    csvHeaders: string[] = [];
  acceptedFiles = state ? state.acceptedFiles : [];
  csvHeaders = state ? state.acceptedCsvHeaders : [];
  const { selectedDataType } = props;
  const setAcceptedFiles = (files: File[]) =>
    dispatch(FileUploadAction.setAcceptedFile(files));

  const [invalidFiles, setInvalidFiles] = useState([] as File[]);

  useEffect(() => {
    setInvalidFiles([]);
    acceptedFiles.forEach((file) => {
      isCsvInvalid(file, csvHeaders).then((isInvalid) => {
        addToInvalidFiles(isInvalid, file);
      });
    });
  }, [acceptedFiles]);

  const addToInvalidFiles = (isInvalid: boolean, file: File) => {
    if (
      isInvalid &&
      file.size <= parseFileSize(allowedFiles?.maxSize) &&
      file.size > parseFileSize(allowedFiles?.minSize)
    ) {
      setInvalidFiles([...invalidFiles, file]);
    }
  };

  const removeFile = (selectedFile: IFile) => {
    const tempFiles = [...acceptedFiles];
    tempFiles.forEach((file: IFile, index: number) => {
      removeFileFromTempFiles(tempFiles, file, selectedFile, index);
    });
    setAcceptedFiles([...tempFiles]);
  };

  const removeFileFromTempFiles = (
    tempFiles: IFile[],
    file: IFile,
    selectedFile: IFile,
    index: number
  ) => {
    if (
      file.lastModified === selectedFile.lastModified &&
      file.name === selectedFile.name &&
      file.preview === selectedFile.preview
    ) {
      tempFiles.splice(index, 1);
    }
  };

  const allowedFiles = ManualFileUploadTypeList.find(
    (r) => r.type === selectedDataType
  )?.allowedFiles[0];

  const selectedDataTypeName = ManualFileUploadTypeList.find(
    (r) => r.type === selectedDataType
  )?.name;

  return (
    <div>
      <div className="top-upload-note">
        {t(d.UPLOADFILES)} {selectedDataTypeName}
      </div>
      <div
        className={
          acceptedFiles.length === 0
            ? 'upload-modal-filedrop'
            : 'updated-upload-filedrop'
        }
      >
        <FileDrop
          label=""
          acceptedTypes={getFileAcceptedTypes(selectedDataType)}
          // maxSize={maxUploadFileSize}
          error={t(d.UPLOADNOTE, {
            type: allowedFiles?.type,
            size: allowedFiles
              ? allowedFiles?.maxSize / MAGIC_VALUE.MEGABYTES
              : MAGIC_VALUE.FIFTY
          })}
          placeholder={t(d.FILEUPLOADPLACEHOLDER)}
          onAccepted={(files) => {
            setAcceptedFiles([...acceptedFiles, ...files]);
          }}
        ></FileDrop>
      </div>
      {acceptedFiles && acceptedFiles.length > 0 && (
        <div className="file-count">{acceptedFiles.length} Files:</div>
      )}
      <div className="file-names">
        {acceptedFiles &&
          acceptedFiles.map((file: File, key: number) => (
            <div className="file-uploaded" key={`uploadedfile-${key}`}>
              <div className="full-width uploaded-file-details">
                <div className="w-6">
                  <Icon root="common" name="document-report" size="small" />
                </div>
                <div className="w-79">
                  {file.name} ({formatBytes(file.size, fileUploadSizeDecimal)})
                </div>
                <div className="remove-button w-15 pl-5">
                  <Button
                    type="link"
                    content={t(d.REMOVE)}
                    onClick={() => removeFile(file as IFile)}
                  />
                </div>
              </div>
              {selectedDataType &&
                file.size <= parseFileSize(allowedFiles?.minSize) && (
                  <div className="err-msg">{t(d.FILEEMPTYERRORMSG)}</div>
                )}
              {selectedDataType &&
                file.size > parseFileSize(allowedFiles?.maxSize) && (
                  <div className="err-msg">{t(d.FILESIZEERRORMSG)}</div>
                )}
              {invalidFiles.includes(file) && (
                <div className="err-msg">{t(d.CSVFILEINVALID)}</div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

export default UploadTab;
